import { useState, useEffect, useRef, FormEvent } from 'react';
import { useSearchParams } from '../navigation';
import type { OAuthProvidersSchema } from '@insforge/shared-schemas';
import { useInsforge } from '../provider/InsforgeProvider';
import { usePublicAuthConfig } from '../hooks/usePublicAuthConfig';
import { SignInForm } from './forms/SignInForm';
import {
  isHostedAuthEnvironment,
  buildLegacyAuthUrl,
  getCsrfTokenFromCookie,
} from '../lib/hosted-auth';

type SignInStep = 'form' | 'awaiting-verification';

export interface SignInProps {
  title?: string;
  subtitle?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  passwordLabel?: string;
  passwordPlaceholder?: string;
  forgotPasswordText?: string;
  forgotPasswordUrl?: string;
  submitButtonText?: string;
  loadingButtonText?: string;
  signUpText?: string;
  signUpLinkText?: string;
  signUpUrl?: string;
  dividerText?: string;
  onError?: (error: Error) => void;
}

/**
 * Pre-built sign-in component with full authentication logic.
 *
 * @component
 * @example
 * ```tsx
 * <SignIn
 *   onError={(error) => console.error('Error:', error)}
 * />
 * ```
 */
export function SignIn({ onError, ...uiProps }: SignInProps) {
  const { signIn, verifyEmail, loginWithOAuth, isSignedIn, getSession, afterSignInUrl } =
    useInsforge();
  const { authConfig } = usePublicAuthConfig();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<SignInStep>('form');
  const [oauthLoading] = useState<OAuthProvidersSchema | null>(null);

  const searchParams = useSearchParams();
  const redirectUrl = searchParams.get('redirect');

  // Track if we're handling OAuth completion redirect
  const isHandlingOAuthRedirectRef = useRef(false);

  // Handle OAuth completion: redirect back to user app with legacy flow params
  useEffect(() => {
    async function handleOAuthComplete() {
      // Skip if not signed in or already handling
      if (!isSignedIn || isHandlingOAuthRedirectRef.current) return;

      const isHosted = isHostedAuthEnvironment();

      if (isHosted && redirectUrl) {
        // Hosted auth: redirect back to user app with legacy flow params
        isHandlingOAuthRedirectRef.current = true;

        try {
          const session = await getSession();
          if (!session) {
            isHandlingOAuthRedirectRef.current = false;
            return;
          }

          const csrfToken = getCsrfTokenFromCookie();
          const legacyUrl = buildLegacyAuthUrl(redirectUrl, {
            accessToken: session.accessToken,
            userId: session.user.id,
            email: session.user.email,
            name: session.user.profile?.name || '',
            csrfToken: csrfToken || undefined,
          });

          window.location.href = legacyUrl;
        } catch (err) {
          console.error('Failed to redirect after OAuth:', err);
          isHandlingOAuthRedirectRef.current = false;
        }
      } else if (!isHosted) {
        // Custom auth: redirect to afterSignInUrl or redirectUrl
        const finalUrl = redirectUrl || afterSignInUrl || '/';
        window.location.href = new URL(finalUrl, window.location.origin).toString();
      }
    }

    void handleOAuthComplete();
  }, [isSignedIn, redirectUrl, getSession, afterSignInUrl]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await signIn(email, password);
      if ('error' in result) {
        // Check if email verification is required (403 status code)
        if (result.statusCode === 403) {
          setStep('awaiting-verification');
          setLoading(false);
          return;
        }
        throw new Error(result.error);
      }
      const { user, accessToken, redirectTo, csrfToken } = result;
      if (user) {
        // Send back credentials to the redirect URL if no verification is required
        const finalUrl = new URL(redirectTo || redirectUrl || '', window.location.origin);
        finalUrl.searchParams.set('access_token', accessToken);
        finalUrl.searchParams.set('user_id', user.id);
        finalUrl.searchParams.set('email', user.email);
        finalUrl.searchParams.set('name', user.profile?.name || '');
        if (csrfToken) {
          finalUrl.searchParams.set('csrf_token', csrfToken);
        }
        window.location.href = finalUrl.toString();
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Sign in failed';
      setError(errorMessage);
      if (onError) {
        onError(new Error(errorMessage));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(code: string) {
    setError('');
    try {
      const result = await verifyEmail(code, email);

      if ('error' in result) {
        throw new Error('Verification failed');
      }

      // Send back credentials to the redirect URL if verification code is successful
      const finalUrl = new URL(result.redirectTo || redirectUrl || '', window.location.origin);
      finalUrl.searchParams.set('access_token', result.accessToken);
      finalUrl.searchParams.set('user_id', result.user.id);
      finalUrl.searchParams.set('email', result.user.email);
      finalUrl.searchParams.set('name', result.user.profile?.name || '');
      if (result.csrfToken) {
        finalUrl.searchParams.set('csrf_token', result.csrfToken);
      }
      window.location.href = finalUrl.toString();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid verification code';
      setError(errorMessage);
    }
  }

  function handleOAuth(provider: OAuthProvidersSchema) {
    try {
      // Always use current page URL as redirect_uri
      // This ensures OAuth callback returns to this page, where PKCE can be completed
      // (sessionStorage is per-origin, so callback must return to same origin)
      void loginWithOAuth(provider, window.location.href);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'OAuth login failed';
      setError(errorMessage);
      if (onError) {
        onError(new Error(errorMessage));
      }
    }
  }

  // If email auth is disabled after loading, don't show the form
  if (!authConfig) {
    return null;
  }

  return (
    <SignInForm
      email={email}
      password={password}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onSubmit={(e) => void handleSubmit(e)}
      error={error}
      loading={loading}
      oauthLoading={oauthLoading}
      onOAuthClick={handleOAuth}
      authConfig={authConfig}
      showVerificationStep={step === 'awaiting-verification'}
      onVerifyCode={handleVerifyCode}
      {...uiProps}
    />
  );
}
